import { createLocation, createPath, History, Location } from 'history';

// anything at or below space, plus DEL and backslash. Browsers strip tabs and newlines from urls and
// read a backslash as a slash, either of which can turn '/\t/evil.com' into '//evil.com'
const UNSAFE_PATHNAME_CHARS = /[\u0000- \u007f\\]/;
// a query or hash may hold spaces (the browser encodes them), but never controls or backslashes
const UNSAFE_QUERY_CHARS = /[\u0000-\u001f\u007f\\]/;

const hasDotSegment = (pathname: string) => pathname.split('/').some((segment) => segment === '.' || segment === '..');

/*
 * Parses the path the way history does when it navigates (it decodeURI's the pathname, which throws
 * on a malformed escape), then checks the location it would actually write to the address bar.
 */
const toSafeLocation = (history: History, path: string): Location | undefined => {
	if (!path.startsWith('/') || UNSAFE_QUERY_CHARS.test(path)) return undefined;

	let location: Location;
	try {
		location = createLocation(path);
	} catch {
		return undefined;
	}

	const { pathname } = location;
	if (pathname.startsWith('//') || UNSAFE_PATHNAME_CHARS.test(pathname) || hasDotSegment(pathname)) {
		return undefined;
	}

	try {
		if (new URL(history.createHref(location), window.location.origin).origin !== window.location.origin) {
			return undefined;
		}
	} catch {
		return undefined;
	}

	return location;
};

/**
 * Builds the window.__kgNavigate function: navigates like a router Link would and returns true, or
 * returns false for anything that is not a safe same-origin path. It never throws.
 */
export const createKgNavigate =
	(history: History) =>
	(path: unknown): boolean => {
		if (typeof path !== 'string') return false;

		const location = toSafeLocation(history, path);
		if (!location) return false;

		try {
			// react-router-dom's Link replaces instead of pushing a duplicate entry for the current page;
			// unlike Link, keep the current state, which holds things like the login return path. Both
			// calls take the raw path, as a Link does: history decodes a location object's pathname
			// again, so passing the parsed one would turn '%2525' into '%'
			if (createPath(location) === createPath(history.location)) {
				history.replace(path, history.location.state);
			} else {
				history.push(path);
			}
		} catch {
			return false;
		}
		return true;
	};
